import { ISlideData } from '@tencent/slide-sdk-model';
import { cloneDeep } from 'lodash';
import { marked } from 'marked';
import { BlockTokens, Token } from './define/token';
import { ISlideType, SlideType } from './define';
import { generator } from './generator';
import { hasImage, isImageOnly } from './utils/token-utils';
import { findCandidates, generateSlideFromTemplate } from './render-slide-data';

// const placeholder = 'https://docimg8.docs.qq.com/image/AgAABTW7Ya4dU-c28eZGOry4TAxcAokL.gif?w=1920&h=1080';
const placeholder = 'https://docimg1.docs.qq.com/image/AgAAEfW2Ng52vh4i6sNFUbYJUA4moApU.gif?w=1920&h=1080';

export interface PageOptions {
  pageCount: number;
  hasChart?: boolean;
}

/** 将 tokens 转换为 slide 页数据 */
export function parsePage(tokens: Token[], options: PageOptions = { pageCount: 1, hasChart: false }): ISlideData[] {
  tokens = tokens.filter((t) => t.type !== 'space' && t.type !== 'hr' && t.type !== 'html');
  if (tokens.length === 0) return [];

  const token = tokens[0];

  // h1/h2 格式固定
  // h1 [+ p] => cover
  // h2 [+ p] => session
  if (token.type === 'heading' && token.depth <= 2) {
    const isSession = token.depth === 2;

    if (isSession) generator.catalogueTexts.push(token.text);

    const struct: Struct = {};
    struct.title = token;
    if (hasImage(token)) {
      struct.image = getImageInToken(token)[0];
    } else if (tokens[1]?.type === 'paragraph') {
      struct.content = struct.content || [];
      struct.content.push(token[1]);
      if (hasImage(tokens[1])) {
        struct.image = getImageInToken(tokens[1])[0];
      }
    }

    return generateSlide(struct, isSession ? SlideType.session : SlideType.cover, options);
  }

  const pages = createPages(tokens);
  return pages.flatMap((p) => generateSlide(p, SlideType.page, options));
}

export const parseTable = (token: marked.Tokens.Table): string[][] => {
  const headers: string[] = token.header.map((h) => h.text);
  const rows = token.rows.map((row) => row.map((cell) => cell.text));
  return [headers, ...rows];
};

export const createPages = (_tokens: Token[]): Struct[] => {
  const tokens = [..._tokens];

  const pages: Struct[] = [];

  while (tokens.length > 0) {
    const struct: Struct = {};
    const [first, second, third] = tokens as BlockTokens[];
    if (first.type === 'heading' && first.depth === 3) {
      struct.title = first;
      if (hasImage(first)) {
        struct.image = getImageInToken(first)[0];
        if (!struct.image.href) {
          struct.image.href = placeholder;
        }
      }
      tokens.shift();

      if (!second) {
        pages.push(struct);
        continue;
      }

      if (second?.type === 'heading' && second.depth <= 3) {
        pages.push(struct);
        continue;
      }

      if (second?.type === 'table') {
        pages.push(struct);
        tokens.shift();
        continue;
      }

      if (second?.type === 'list') {
        // TODO:
        const left = assignList(second, struct);
        tokens.shift();
        pages.push(struct);
        if (left) {
          left.forEach((tokens) => pages.push(...createPages(tokens)));
        }
        continue;
      }
      // if (second.type === 'paragraph' || second.type === 'paragraph' || second.type === 'blockquote' || second.type === 'code') {
      // }
      if (hasImage(second)) {
        struct.image = getImageInToken(second)[0];
        if (!isImageOnly(second)) {
          struct.content = struct.content || [];
          struct.content.push(second);
        }
      } else {
        struct.content = struct.content || [];
        struct.content.push(second);
      }

      tokens.shift();

      let left;
      if (third?.type === 'list') {
        left = assignList(third, struct);
        tokens.shift();
      }

      pages.push(struct);
      if (left) {
        left.forEach((tokens) => pages.push(...createPages(tokens)));
      }
      continue;
    }

    if (first.type === 'list') {
      const left = assignList(first, struct);
      tokens.shift();
      pages.push(struct);
      if (left) {
        left.forEach((tokens) => pages.push(...createPages(tokens)));
      }
      continue;
    }

    // 未命中，丢弃
    tokens.shift();
  }

  return pages;
};

export interface StructBase {
  title?: marked.Tokens.Heading | marked.Tokens.Paragraph | marked.Tokens.Text | marked.Tokens.Tag; // code quote
  content?: Array<
    | marked.Tokens.Heading
    | marked.Tokens.Paragraph
    | marked.Tokens.Text
    | marked.Tokens.List
    | marked.Tokens.Code
    | marked.Tokens.Blockquote
  >;
  image?: marked.Tokens.Image;
}

export interface StructWithList extends StructBase {
  list?: StructBase[];
}

export interface Struct extends StructBase {
  list?: marked.Tokens.List;
}

export const getImageInToken = (token: Token): marked.Tokens.Image[] => {
  if (token.type === 'image') return [token];

  if ('tokens' in token && token.tokens?.length) {
    return (token.tokens as Token[]).flatMap(getImageInToken);
  }

  return [];
};

type ListItem = Struct;

export const assignList = (token: marked.Tokens.List, struct: Struct) => {
  if (!hasImage(token) && token.items.length === 1) {
    struct.content = struct.content || [];
    struct.content.push(token);
    return;
  }

  if (ifListValid(token)) {
    struct.list = token;
    return;
  }

  const { list, left } = cutList(token);
  struct.content = struct.content || [];
  struct.content.push(list);
  return left;
};

const ifListValid = (token: marked.Tokens.List) => {
  const listItems = splitListIntoGroup(token);
  return isStructureEqual(listItems);
};

// 当前只有遇到图片需要切分页
const shouldBeCut = (token: marked.Tokens.List) => {
  return hasImage(token);
};

const cutList = (token: marked.Tokens.List, left: Token[][] = []): { list: marked.Tokens.List; left: Token[][] } => {
  const t = cloneDeep(token);
  if (!shouldBeCut(t)) {
    return { list: t, left: [] };
  }

  t.items.forEach((listItem) => {
    if (!listItem.tokens) return;
    // FIXME:
    // 默认最多两个 token text/list
    const [maybeText, maybeList] = listItem.tokens;
    if (maybeText.type !== 'text') {
      // unexpected
      return;
    }

    // 没图片也没列表, 就是空页
    if (!hasImage(maybeText as Token) && maybeList === undefined) {
      // donothing
    } else {
      left.push(listItem.tokens as Token[]);
    }

    // 只保留文本, 其余的不管是图片还是 list 都单独分页
    listItem.tokens = [maybeText];
  });

  return {
    list: t,
    left,
  };
};

const splitListIntoGroup = (token: marked.Tokens.List): ListItem[] => {
  const result: ListItem[] = [];
  token.items.forEach((listItem) => {
    const s: ListItem = {};
    if (!listItem.tokens) return;

    listItem.tokens.forEach((token) => {
      if (token.type === 'text' && 'tokens' in token && token.tokens) {
        // as paragraph
        s.title = token as marked.Tokens.Text;
        if (hasImage(token as Token)) {
          // TODO: 目前只能保留一张图
          const imageTokens = getImageInToken(token as Token);
          const [imageToken] = imageTokens;
          // 生图场景，先填入占位符图片
          if (!imageToken.href) {
            imageToken.href = placeholder;
          }
          s.image = imageToken;
        }
        return;
      }

      if (token.type === 'list') {
        // list 内容检查
        const { list, left } = cutList(token as marked.Tokens.List);
        s.content = s.content || [];
        s.content.push(list);
      }
    });

    // 确保不全为空
    if (s.content || s.title) result.push(s);
  });

  return result;
};

function isStructureEqual(arr: any[]): boolean {
  if (arr.length <= 1) {
    return true;
  }
  const firstItem = arr[0];
  for (let i = 1; i < arr.length; i++) {
    const currentItem = arr[i];
    if (typeof firstItem !== typeof currentItem) {
      return false;
    }
    if (typeof firstItem === 'object' && firstItem !== null && currentItem !== null) {
      // TODO: token 只比较 type
      if (firstItem.type && firstItem.type === currentItem.type) return true;

      if (Object.keys(firstItem).length !== Object.keys(currentItem).length) return false;

      for (const key of Object.keys(firstItem)) {
        if (!isStructureEqual([firstItem[key], currentItem[key]])) {
          return false;
        }
      }
    }
  }
  return true;
}

const deleteItemInList = <T>(item: T, list: T[]) => {
  const idx = list.findIndex((i) => i === item);
  if (idx === -1) return;
  list.splice(idx, 1);
};

const splitList = <T>(list: T[], maxCount: number): T[][] => {
  const l = [...list];
  const result: T[][] = [];
  while (l.length > 0) {
    result.push(l.splice(0, maxCount));
  }
  return result;
};

const transStruct = (struct: Struct): StructWithList => {
  return {
    ...struct,
    list: struct.list && splitListIntoGroup(struct.list),
  };
};

export const generateSlide = (
  page: Struct,
  type: SlideType,
  options: PageOptions = { pageCount: 1, hasChart: false },
): ISlideData[] => {
  const { pageCount, hasChart } = options;
  // TODO:
  const ignoreImage = pageCount === 1;

  let allCandidates = generator.templateByType[type as ISlideType];
  // TODO:
  allCandidates = allCandidates.filter(
    (candidate) => (hasChart && candidate.tag.idMap.chart) || (!hasChart && !candidate.tag.idMap.chart),
  );

  let struct = transStruct(page);
  let candidates = findCandidates(struct, allCandidates, type, { ignoreImage });

  // 尝试再找一次
  if (candidates.length === 0) {
    if (type === SlideType.cover || type === SlideType.session || type === SlideType.ending) {
      // 这三种页，如果找不到，就直接用所有
      candidates = allCandidates;
    } else if (type === SlideType.catalogue) {
      if (struct.list) {
        for (let margin = 1; margin < struct.list.length - 1; margin++) {
          const res = findCandidates(struct, allCandidates, type, { lengthMargin: margin, ignoreImage });
          if (res.length === 0) continue;

          candidates = res;
          break;
        }
      }
    } else {
      // type === SlideType.page
      // 列表 fallback 段落
      if (page.list && !page.content) {
        page.content = [page.list];
        page.list = undefined;
        struct = transStruct(page);
        candidates = findCandidates(struct, allCandidates, type, { ignoreImage });
      }
    }
  }

  if (candidates.length === 0) {
    console.error('unsupported', page, SlideType[type]);
    return [];
  }

  // console.debug('Candidates length:', candidate.length);
  let finalCandidates = candidates;
  if (pageCount <= 0 || pageCount >= candidates.length) {
    finalCandidates = candidates;
  } else {
    finalCandidates = generator.getCandidates(candidates, pageCount);
  }

  return finalCandidates.flatMap((template) =>
    generateSlideFromTemplate(
      struct,
      type,
      template,
      generator.layoutIdMappingByType[type],
      generator.userTemplate.resourceMap,
    ),
  );
};
